import React from 'react';
import { Gear, Mnemos, Titan, Weapon, ArmoryLoadout, KratosTable, ArmoryItem } from '../../core/types';
import './DataStorageSection.css';
import { Constants } from '../../core/constants';
import { useConfirm } from '../../hooks/useConfirm';
import { ConfirmModal } from '../../components/ConfirmModal';
import Button from '../../components/Button';
import {
    clearStoredArmoryData,
    getDownloadableArmoryItems,
    mergeUploadedArmoryItems,
    rebindArmoryLoadouts,
} from '../../core/utilities/armory-storage';

interface DataStorageSectionProps {
    setTitans: React.Dispatch<React.SetStateAction<Titan[]>>;
    setKratosTables: React.Dispatch<React.SetStateAction<KratosTable[]>>;
    setWeapons: React.Dispatch<React.SetStateAction<Weapon[]>>;
    setGear: React.Dispatch<React.SetStateAction<Gear[]>>;
    setMnemos: React.Dispatch<React.SetStateAction<Mnemos[]>>;
    setLoadouts: React.Dispatch<React.SetStateAction<ArmoryLoadout[]>>;
    onResetSelections: () => void;
    defaultLoadout: ArmoryLoadout;
    loadouts: ArmoryLoadout[];
    titans: Titan[];
    kratosTables: KratosTable[];
    weapons: Weapon[];
    gear: Gear[];
    mnemos: Mnemos[];
}

const DataStorageSection: React.FC<DataStorageSectionProps> = ({
    setTitans,
    setKratosTables,
    setWeapons,
    setGear,
    setMnemos,
    setLoadouts,
    defaultLoadout,
    onResetSelections,
    loadouts,
    titans,
    kratosTables,
    weapons,
    gear,
    mnemos,
}) => {
    const [uploadError, setUploadError] = React.useState('');

    const { confirm, confirmState, handleConfirm, handleCancel } = useConfirm();

    const currentArmory = { titans, kratosTables, weapons, gear, mnemos };

    const setCollectionItems = (type: 'titans' | 'kratosTables' | 'weapons' | 'gear' | 'mnemos', nextItems: ArmoryItem[]) => {
        const nextArmory = {
            ...currentArmory,
            [type]: nextItems,
        };

        switch (type) {
            case 'titans':
                setTitans(nextItems as Titan[]);
                break;
            case 'kratosTables':
                setKratosTables(nextItems as KratosTable[]);
                break;
            case 'weapons':
                setWeapons(nextItems as Weapon[]);
                break;
            case 'gear':
                setGear(nextItems as Gear[]);
                break;
            case 'mnemos':
                setMnemos(nextItems as Mnemos[]);
                break;
        }

        setLoadouts(rebindArmoryLoadouts(loadouts, nextArmory, defaultLoadout));
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, type: 'titans' | 'kratosTables' | 'weapons' | 'gear' | 'mnemos') => {
        const file = event.target.files?.[0];

        if (file) {
            const typeName = type.charAt(0).toUpperCase() + type.slice(1);
            const confirmUpdate = await confirm(
                `You're about to UPLOAD ${typeName}. This will merge the uploaded ${typeName} with your existing custom armory data. Bundled ${typeName} are read-only, so if any uploaded item name collides with bundled data the entire upload will be rejected. \n\nDo you want to continue?`,
            );
            if (!confirmUpdate) return;

            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const fileData = JSON.parse(e.target?.result as string);
                    if (!Array.isArray(fileData)) {
                        setUploadError(`Upload rejected. ${typeName} files must contain a JSON array.`);
                        return;
                    }

                    const result = mergeUploadedArmoryItems(type, currentArmory[type], fileData);
                    if (result.error || !result.items) {
                        setUploadError(result.error ?? `Upload rejected. ${typeName} could not be merged.`);
                        return;
                    }

                    setCollectionItems(type, result.items);
                    setUploadError('');
                } catch (error) {
                    setUploadError(`Upload rejected. ${typeName} could not be parsed: ${(error as Error).message}`);
                }
            };

            reader.readAsText(file);
        }
    };

    const handleDownload = (type: 'titans' | 'kratosTables' | 'weapons' | 'gear' | 'mnemos') => {
        const dataMap = {
            titans: getDownloadableArmoryItems('titans', titans),
            kratosTables: getDownloadableArmoryItems('kratosTables', kratosTables),
            weapons: getDownloadableArmoryItems('weapons', weapons),
            gear: getDownloadableArmoryItems('gear', gear),
            mnemos: getDownloadableArmoryItems('mnemos', mnemos),
        };

        const data = JSON.stringify(dataMap[type]);
        const blob = new Blob([data], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${type.charAt(0).toUpperCase() + type.slice(1)}.json`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const handleClearData = async () => {
        const confirmUpdate = await confirm(
            `You're about to ❌ DELETE ❌ all armory data. This will reset Titans, Kratos Tables, Weapons, Other Gear, and Mnemos back to the default values and you will lose any items which you uploaded or created manually. You should download the existing data as a backup before continuing. \n\nDo you want to continue?`,
        );
        if (!confirmUpdate) return;

        clearStoredArmoryData(localStorage);

        setUploadError('');

        localStorage.setItem(Constants.LocalStorageKeys.SCHEMA_VERSION, `${Constants.CURRENT_SCHEMA_VERSION}`);
        onResetSelections();
        window.location.reload();
    };

    return (
        <>
            <div className="input-section">
                <div className="panel-section-header">
                    <div className="panel-section-heading">
                        <h3>Data Storage</h3>
                    </div>
                </div>
                <div className="input-group">
                    <div className="loadout-button-container">
                        <Button
                            className="file-upload-button"
                            variant="primary"
                            fullWidth
                            onClick={() => {
                                const inputElement = document.getElementById('upload-titans');
                                if (inputElement) {
                                    inputElement.click();
                                }
                            }}
                        >
                            Upload Titans
                        </Button>
                        <input id="upload-titans" type="file" accept=".json" hidden onChange={(e) => handleFileUpload(e, 'titans')} />
                        <Button
                            className="file-upload-button"
                            variant="primary"
                            fullWidth
                            onClick={() => {
                                const inputElement = document.getElementById('upload-kratos-tables');
                                if (inputElement) {
                                    inputElement.click();
                                }
                            }}
                        >
                            Upload Kratos Tables
                        </Button>
                        <input id="upload-kratos-tables" type="file" accept=".json" hidden onChange={(e) => handleFileUpload(e, 'kratosTables')} />
                        <Button
                            className="file-upload-button"
                            variant="primary"
                            fullWidth
                            onClick={() => {
                                const inputElement = document.getElementById('upload-weapons');
                                if (inputElement) {
                                    inputElement.click();
                                }
                            }}
                        >
                            Upload Weapons
                        </Button>
                        <input id="upload-weapons" type="file" accept=".json" hidden onChange={(e) => handleFileUpload(e, 'weapons')} />
                        <Button
                            className="file-upload-button"
                            variant="primary"
                            fullWidth
                            onClick={() => {
                                const inputElement = document.getElementById('upload-gear');
                                if (inputElement) {
                                    inputElement.click();
                                }
                            }}
                        >
                            Upload Other Gear
                        </Button>
                        <input id="upload-gear" type="file" accept=".json" hidden onChange={(e) => handleFileUpload(e, 'gear')} />
                        <Button
                            className="file-upload-button"
                            variant="primary"
                            fullWidth
                            onClick={() => {
                                const inputElement = document.getElementById('upload-mnemos');
                                if (inputElement) {
                                    inputElement.click();
                                }
                            }}
                        >
                            Upload Mnemos
                        </Button>
                        <input id="upload-mnemos" type="file" accept=".json" hidden onChange={(e) => handleFileUpload(e, 'mnemos')} />
                    </div>

                    <div className="loadout-button-container">
                        <Button className="file-upload-button" variant="primary" fullWidth onClick={() => handleDownload('titans')}>
                            Download Titans
                        </Button>
                        <Button className="file-upload-button" variant="primary" fullWidth onClick={() => handleDownload('kratosTables')}>
                            Download Kratos Tables
                        </Button>
                        <Button className="file-upload-button" variant="primary" fullWidth onClick={() => handleDownload('weapons')}>
                            Download Weapons
                        </Button>
                        <Button className="file-upload-button" variant="primary" fullWidth onClick={() => handleDownload('gear')}>
                            Download Other Gear
                        </Button>
                        <Button className="file-upload-button" variant="primary" fullWidth onClick={() => handleDownload('mnemos')}>
                            Download Mnemos
                        </Button>
                    </div>
                </div>
                {uploadError && <p className="data-storage-error">{uploadError}</p>}
                <div className="spacer" />
                <div className="input-section-button-container">
                    <Button className="clear-storage-button" variant="danger" fullWidth onClick={handleClearData}>
                        Reset Armory
                    </Button>
                </div>
            </div>
            {confirmState && <ConfirmModal message={confirmState.message} onConfirm={handleConfirm} onCancel={handleCancel} />}
        </>
    );
};

export default DataStorageSection;
